import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection } from '../channels/types'
import { Button, Card, ChannelDot, ChannelPill, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, gridColumns, mobileButtonStyle } from '../components/ui'
import { exportChannelInventoryVariance, getChannelInventoryVariance } from '../dashboard/dashboardApi'
import type {
  ChannelConnectionStatus,
  ChannelInventoryVarianceItem,
  ChannelInventoryVarianceReport,
  ChannelInventoryVarianceSeverity,
  ChannelInventoryVarianceSortDirection,
  ChannelInventoryVarianceSortField,
  ChannelInventoryVarianceType,
  Provider,
} from '../dashboard/types'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { listWarehouses } from '../warehouses/warehousesApi'
import type { Warehouse } from '../warehouses/types'

interface ChannelInventoryVariancePageProps {
  accessToken: string
  onBack: () => void
  onOpenProduct: (productId: number) => void
  onOpenStockAllocation: () => void
}

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

interface FilterDraft {
  query: string
  provider: Provider | ''
  channelConnectionId: string
  warehouseId: string
}

interface SortState {
  field: ChannelInventoryVarianceSortField | null
  direction: ChannelInventoryVarianceSortDirection | null
}

interface VarianceLoadState {
  requestKey: string
  report: ChannelInventoryVarianceReport | null
  error: unknown
}

const PAGE_SIZE = 20
const PROVIDERS: Provider[] = ['ZID', 'SALLA', 'AMAZON', 'TRENDYOL', 'NOON']
const PROVIDER_DOT_NAMES: Record<Provider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function numberLocale(locale: string) {
  return locale.startsWith('ar') ? 'ar-SA' : 'en-US'
}

function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 0 }).format(value)
}

function parseChannelConnectionId(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

function statusTone(status: ChannelConnectionStatus): PillTone {
  if (status === 'CONNECTED') return 'success'
  if (status === 'PENDING') return 'info'
  if (status === 'AUTH_EXPIRED' || status === 'ERROR') return 'danger'
  return 'warning'
}

function severityTone(severity: ChannelInventoryVarianceSeverity): PillTone {
  return severity === 'DANGER' ? 'danger' : 'warning'
}

function varianceIcon(type: ChannelInventoryVarianceType) {
  if (type === 'CHANNEL_EXCEEDS_SOURCE_AVAILABILITY') return I.trendD
  if (type === 'NEGATIVE_CHANNEL_QUANTITY') return I.alert
  return I.warehouse
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) return `pages.channelInventoryVariance.errors.${error.code}`
  return 'pages.channelInventoryVariance.errors.REQUEST_FAILED'
}

function SummaryCard({ label, value, tone, icon, subtitle }: { label: string; value: string; tone: PillTone; icon: ReactNode; subtitle: string }) {
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 38, height: 38, borderRadius: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-100)', color: 'var(--brand-700)' }}>
          <Icon size={18}>{icon}</Icon>
        </div>
        <Pill tone={tone} size="sm" dot>{subtitle}</Pill>
      </div>
      <div style={{ marginTop: 14, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 26, lineHeight: 1, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em' }}>{value}</div>
    </Card>
  )
}

export function ChannelInventoryVariancePage({ accessToken, onBack, onOpenProduct, onOpenStockAllocation }: ChannelInventoryVariancePageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const locale = i18n.language
  const [filters, setFilters] = useState<FilterDraft>({ query: '', provider: '', channelConnectionId: '', warehouseId: '' })
  const [appliedFilters, setAppliedFilters] = useState<FilterDraft>({ query: '', provider: '', channelConnectionId: '', warehouseId: '' })
  const [filterOptions, setFilterOptions] = useState<{ channels: ChannelConnection[]; warehouses: Warehouse[] } | null>(null)
  const [filterOptionsError, setFilterOptionsError] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [sort, setSort] = useState<SortState>({ field: null, direction: null })
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  const requestKey = JSON.stringify({ accessToken, appliedFilters, pageIndex, sort, refreshNonce })
  const [loadState, setLoadState] = useState<VarianceLoadState>({ requestKey: '', report: null, error: null })

  useEffect(() => {
    let cancelled = false
    Promise.all([listChannelConnections(accessToken), listWarehouses(accessToken)])
      .then(([channels, warehouses]) => {
        if (cancelled) return
        setFilterOptions({ channels, warehouses })
        setFilterOptionsError(false)
      })
      .catch(() => { if (!cancelled) setFilterOptionsError(true) })
    return () => { cancelled = true }
  }, [accessToken, refreshNonce])

  useEffect(() => {
    let cancelled = false

    getChannelInventoryVariance(accessToken, {
      page: pageIndex,
      size: PAGE_SIZE,
      query: appliedFilters.query,
      provider: appliedFilters.provider,
      channelConnectionId: parseChannelConnectionId(appliedFilters.channelConnectionId),
      warehouseId: parseChannelConnectionId(appliedFilters.warehouseId),
      sortBy: sort.field ?? undefined,
      sortDirection: sort.direction ?? undefined,
    })
      .then(nextReport => {
        if (cancelled) return
        setLoadState({ requestKey, report: nextReport, error: null })
        if (nextReport.page !== pageIndex) setPageIndex(nextReport.page)
      })
      .catch(caught => {
        if (!cancelled) setLoadState({ requestKey, report: null, error: caught })
      })

    return () => { cancelled = true }
  }, [accessToken, appliedFilters, pageIndex, refreshNonce, requestKey, sort])

  const report = loadState.requestKey === requestKey ? loadState.report : null
  const error = loadState.requestKey === requestKey ? loadState.error : null
  const loading = loadState.requestKey !== requestKey

  const handleExport = async () => {
    setExporting(true)
    setExportError(false)
    try {
      const download = await exportChannelInventoryVariance(accessToken, {
        query: appliedFilters.query,
        provider: appliedFilters.provider,
        channelConnectionId: parseChannelConnectionId(appliedFilters.channelConnectionId),
        warehouseId: parseChannelConnectionId(appliedFilters.warehouseId),
        sortBy: sort.field ?? undefined,
        sortDirection: sort.direction ?? undefined,
      })
      const objectUrl = URL.createObjectURL(download.blob)
      try {
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = download.filename ?? 'channel-inventory-variance.xlsx'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
      } finally {
        URL.revokeObjectURL(objectUrl)
      }
    } catch {
      setExportError(true)
    } finally {
      setExporting(false)
    }
  }

  const applyFilters = (event: FormEvent) => {
    event.preventDefault()
    setPageIndex(0)
    setAppliedFilters(filters)
  }

  const resetFilters = () => {
    const blank = { query: '', provider: '' as const, channelConnectionId: '', warehouseId: '' }
    setFilters(blank)
    setAppliedFilters(blank)
    setPageIndex(0)
  }

  const changeSort = (field: ChannelInventoryVarianceSortField) => {
    setPageIndex(0)
    setSort(current => ({
      field,
      direction: current.field === field && current.direction === 'DESC' ? 'ASC' : 'DESC',
    }))
  }

  const formatDate = (value?: string | null) => {
    if (!value) return t('pages.channelInventoryVariance.neverSeen')
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(numberLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }

  const renderItemActions = (item: ChannelInventoryVarianceItem) => (
    <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, justifyContent: isMobile ? undefined : 'flex-end' }}>
      <Button kind="ghost" size="sm" icon={I.eye} onClick={() => onOpenProduct(item.productId)} style={mobileButtonStyle(isMobile)}>
        {t('pages.channelInventoryVariance.actions.viewProduct')}
      </Button>
      <Button kind="secondary" size="sm" icon={I.layers} onClick={onOpenStockAllocation} style={mobileButtonStyle(isMobile)}>
        {t('pages.channelInventoryVariance.actions.stockAllocation')}
      </Button>
    </div>
  )

  const renderSortableHeader = (field: ChannelInventoryVarianceSortField, label: string) => {
    const active = sort.field === field
    const ariaSort = active ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'
    const sortLabelKey = active ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'
    return (
      <th
        key={field}
        aria-sort={ariaSort}
        style={{ textAlign: 'start', paddingBlock: 7, paddingInline: 10, color: 'var(--fg-subtle)' }}
      >
        <Button
          kind="ghost"
          size="xs"
          ariaLabel={String(t(`pages.channelInventoryVariance.sort.${sortLabelKey}`, { column: label }))}
          onClick={() => changeSort(field)}
          style={{ paddingBlock: 4, paddingInline: 4, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: active ? 'var(--brand-700)' : 'var(--fg-subtle)' }}
        >
          <span>{label}</span>
          <span aria-hidden="true" style={{ display: 'inline-flex', transform: active && sort.direction === 'ASC' ? 'rotate(180deg)' : undefined }}>
            <Icon size={13}>{I.chevronD}</Icon>
          </span>
        </Button>
      </th>
    )
  }

  const pageStart = report && report.totalElements > 0 ? report.page * report.size + 1 : 0
  const pageEnd = report ? Math.min(report.totalElements, report.page * report.size + report.items.length) : 0
  const hasPreviousPage = Boolean(report && report.page > 0)
  const hasNextPage = Boolean(report && report.page + 1 < report.totalPages)

  if (loading && report == null) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <Button kind="ghost" size="xs" icon={i18n.dir() === 'rtl' ? I.arrowR : I.arrowL} onClick={onBack} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.back')}</Button>
        <Card padding={22}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('pages.channelInventoryVariance.loading')}
          </div>
        </Card>
      </div>
    )
  }

  if (error != null && report == null) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <Button kind="ghost" size="xs" icon={i18n.dir() === 'rtl' ? I.arrowR : I.arrowL} onClick={onBack} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.back')}</Button>
        <Card padding={22} style={{ borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--danger)', fontSize: 13, fontWeight: 800 }}>
              <Icon size={16}>{I.alert}</Icon>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.channelInventoryVariance.errors.REQUEST_FAILED')) }))}
            </div>
            <Button kind="primary" size="sm" icon={I.refresh} onClick={() => setRefreshNonce(value => value + 1)} style={mobileButtonStyle(isMobile)}>
              {t('pages.channelInventoryVariance.actions.retry')}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16, paddingBottom: 48 }}>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 14, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <Button kind="ghost" size="xs" icon={i18n.dir() === 'rtl' ? I.arrowR : I.arrowL} onClick={onBack} style={{ marginBottom: 10, ...mobileButtonStyle(isMobile) }}>
            {t('pages.channelInventoryVariance.back')}
          </Button>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--brand-900)' }}>{t('pages.channelInventoryVariance.title')}</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', maxWidth: 780, lineHeight: 1.55, ...TEXT_WRAP_STYLE }}>
            {t('pages.channelInventoryVariance.description')}
          </p>
        </div>
        {report && (
          <Pill tone="brand" size="sm">{t('pages.channelInventoryVariance.generatedAt', { value: formatDate(report.generatedAt) })}</Pill>
        )}
      </div>

      {error != null && report != null && (
        <Card padding={14} style={{ borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
            <Icon size={16} color="var(--danger)">{I.alert}</Icon>
            <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: 13, ...TEXT_WRAP_STYLE }}>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.channelInventoryVariance.errors.REQUEST_FAILED')) }))}
            </div>
            <Button kind="ghost" size="sm" icon={I.refresh} onClick={() => setRefreshNonce(value => value + 1)}>{t('pages.channelInventoryVariance.actions.retry')}</Button>
          </div>
        </Card>
      )}

      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 12 }}>
            <SummaryCard label={t('pages.channelInventoryVariance.summary.total')} value={formatNumber(report.summary.totalVarianceListings, locale)} tone={report.summary.totalVarianceListings > 0 ? 'warning' : 'success'} icon={I.alert} subtitle={t('pages.channelInventoryVariance.summary.live')} />
            <SummaryCard label={t('pages.channelInventoryVariance.summary.danger')} value={formatNumber(report.summary.dangerCount, locale)} tone={report.summary.dangerCount > 0 ? 'danger' : 'neutral'} icon={I.zap} subtitle={t('pages.channelInventoryVariance.severity.DANGER')} />
            <SummaryCard label={t('pages.channelInventoryVariance.summary.warning')} value={formatNumber(report.summary.warningCount, locale)} tone={report.summary.warningCount > 0 ? 'warning' : 'neutral'} icon={I.trendD} subtitle={t('pages.channelInventoryVariance.severity.WARNING')} />
            <SummaryCard label={t('pages.channelInventoryVariance.summary.excluded')} value={formatNumber(report.summary.infiniteExcludedCount + report.summary.unknownStockCount, locale)} tone="info" icon={I.layers} subtitle={t('pages.channelInventoryVariance.summary.excludedSub', { infinite: formatNumber(report.summary.infiniteExcludedCount, locale), unknown: formatNumber(report.summary.unknownStockCount, locale) })} />
          </div>

          <Card padding={18} style={{ borderColor: 'var(--brand-300)', background: 'var(--brand-100)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, '22px minmax(0, 1fr) auto'), gap: 12, alignItems: 'start' }}>
              <Icon size={18} color="var(--brand-700)">{I.shield}</Icon>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: 'var(--brand-900)', fontSize: 14 }}>{t('pages.channelInventoryVariance.remediation.title')}</div>
                <div style={{ color: 'var(--brand-900)', fontSize: 12.5, marginTop: 4, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>{t('pages.channelInventoryVariance.remediation.body')}</div>
              </div>
              <Button kind="secondary" size="sm" icon={I.layers} onClick={onOpenStockAllocation} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.actions.stockAllocation')}</Button>
            </div>
          </Card>

          <Card padding={0}>
            {exportError && <div role="alert" style={{ padding: 18, color: 'var(--danger)' }}>{t('pages.channelInventoryVariance.export.error')}</div>}
            {filterOptionsError && <div role="alert" style={{ padding: 18, color: 'var(--danger)' }}>{t('pages.channelInventoryVariance.filters.loadError')} <Button kind="ghost" size="xs" onClick={() => setRefreshNonce(value => value + 1)}>{t('pages.channelInventoryVariance.actions.retry')}</Button></div>}
            <form onSubmit={applyFilters} style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(4, minmax(0, 1fr))', gap: 10, alignItems: 'end' }}>
              <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelInventoryVariance.filters.query')}</span>
                <input
                  value={filters.query}
                  onChange={event => setFilters(current => ({ ...current, query: event.target.value }))}
                  placeholder={t('pages.channelInventoryVariance.filters.queryPlaceholder')}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 12, paddingBlock: 9, paddingInline: 12, font: 'inherit', color: 'var(--fg)', background: 'white' }}
                />
              </label>
              <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelInventoryVariance.filters.provider')}</span>
                <select
                  value={filters.provider}
                  onChange={event => setFilters(current => ({ ...current, provider: event.target.value as Provider | '', channelConnectionId: '' }))}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 12, paddingBlock: 9, paddingInline: 12, font: 'inherit', color: 'var(--fg)', background: 'white' }}
                >
                  <option value="">{t('pages.channelInventoryVariance.filters.allProviders')}</option>
                  {PROVIDERS.map(provider => <option key={provider} value={provider}>{t(`channels.providers.${provider}`)}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelInventoryVariance.filters.connection')}</span>
                <select
                  value={filters.channelConnectionId}
                  disabled={filterOptions === null}
                  onChange={event => setFilters(current => ({ ...current, channelConnectionId: event.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 12, paddingBlock: 9, paddingInline: 12, font: 'inherit', color: 'var(--fg)', background: 'white' }}
                >
                  <option value="">{t('pages.channelInventoryVariance.filters.allChannels')}</option>
                  {(filterOptions?.channels ?? []).filter(channel => !filters.provider || channel.provider === filters.provider).map(channel => <option key={channel.id} value={channel.id}>{channel.displayName} · {t(`channels.providers.${channel.provider}`)}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelInventoryVariance.filters.warehouse')}</span>
                <select
                  value={filters.warehouseId}
                  disabled={filterOptions === null}
                  onChange={event => setFilters(current => ({ ...current, warehouseId: event.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 12, paddingBlock: 9, paddingInline: 12, font: 'inherit', color: 'var(--fg)', background: 'white' }}
                >
                  <option value="">{t('pages.channelInventoryVariance.filters.allWarehouses')}</option>
                  {(filterOptions?.warehouses ?? []).map(warehouse => <option key={warehouse.id} value={warehouse.id}>{warehouse.name} · {warehouse.code}</option>)}
                </select>
              </label>
              <div style={{ gridColumn: '1 / -1', display: isMobile ? 'grid' : 'flex', gap: 10 }}>
                <Button kind="primary" size="sm" icon={I.search} type="submit" disabled={loading} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.actions.applyFilters')}</Button>
                <Button kind="ghost" size="sm" icon={I.x} onClick={resetFilters} disabled={loading} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.actions.clearFilters')}</Button>
                <Button kind="secondary" size="sm" icon={I.download} onClick={handleExport} disabled={loading || exporting} style={{ marginInlineStart: isMobile ? 0 : 'auto', ...mobileButtonStyle(isMobile) }}>{t(exporting ? 'pages.channelInventoryVariance.export.exporting' : 'pages.channelInventoryVariance.export.action')}</Button>
              </div>
            </form>

            {loading ? (
              <div style={{ padding: 28, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>{t('pages.channelInventoryVariance.loading')}</div>
            ) : report.items.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                <div style={{ fontWeight: 800, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.channelInventoryVariance.empty.title')}</div>
                <div>{t('pages.channelInventoryVariance.empty.body')}</div>
              </div>
            ) : isMobile ? (
              <div style={{ display: 'grid', gap: 10, padding: 12 }}>
                {report.items.map(item => (
                  <Card key={item.listingId} padding={14}>
                    <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
                      <ChannelDot name={PROVIDER_DOT_NAMES[item.provider]} size={32} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <strong style={{ color: 'var(--fg)', fontSize: 14, ...TEXT_WRAP_STYLE }}>{item.productName}</strong>
                          <Pill tone={severityTone(item.severity)} size="sm" dot>{t(`pages.channelInventoryVariance.severity.${item.severity}`)}</Pill>
                        </div>
                        <div style={{ marginTop: 3, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11.5, ...TEXT_WRAP_STYLE }}>{item.productSku}</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 9, marginTop: 12, color: 'var(--fg-muted)', fontSize: 12.5 }}>
                      <Pill tone="neutral" size="sm"><Icon size={13}>{varianceIcon(item.varianceType)}</Icon>{t(`pages.channelInventoryVariance.types.${item.varianceType}`)}</Pill>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <ChannelPill name={PROVIDER_DOT_NAMES[item.provider]} size="sm" />
                        <Pill tone={statusTone(item.connectionStatus)} size="sm">{t(`channels.status.${item.connectionStatus}`)}</Pill>
                        <span style={{ ...TEXT_WRAP_STYLE }}>{item.channelDisplayName}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                        <div><strong style={{ display: 'block', color: 'var(--fg)' }}>{formatNumber(item.sourceAvailableQty, locale)}</strong><span>{t('pages.channelInventoryVariance.table.source')}</span></div>
                        <div><strong style={{ display: 'block', color: 'var(--fg)' }}>{formatNumber(item.channelFiniteQuantity, locale)}</strong><span>{t('pages.channelInventoryVariance.table.channel')}</span></div>
                        <div><strong style={{ display: 'block', color: 'var(--fg)' }}>{formatNumber(item.differenceQty, locale)}</strong><span>{t('pages.channelInventoryVariance.table.difference')}</span></div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, ...TEXT_WRAP_STYLE }}>{item.listingSku || item.externalProductId || t('pages.channelInventoryVariance.noSku')}</div>
                      <div>{t('pages.channelInventoryVariance.lastSeen', { value: formatDate(item.lastSeenAt) })}</div>
                      {renderItemActions(item)}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1120 }}>
                  <thead>
                    <tr style={{ background: 'var(--ink-50)' }}>
                      <th style={{ textAlign: 'start', paddingBlock: 11, paddingInline: 14, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.channelInventoryVariance.table.product')}</th>
                      <th style={{ textAlign: 'start', paddingBlock: 11, paddingInline: 14, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.channelInventoryVariance.table.channelListing')}</th>
                      {renderSortableHeader('SOURCE', t('pages.channelInventoryVariance.table.source'))}
                      {renderSortableHeader('CHANNEL', t('pages.channelInventoryVariance.table.channel'))}
                      {renderSortableHeader('DELTA', t('pages.channelInventoryVariance.table.difference'))}
                      <th style={{ textAlign: 'start', paddingBlock: 11, paddingInline: 14, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.channelInventoryVariance.table.variance')}</th>
                      <th style={{ textAlign: 'start', paddingBlock: 11, paddingInline: 14, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.channelInventoryVariance.table.lastSeen')}</th>
                      <th style={{ textAlign: 'start', paddingBlock: 11, paddingInline: 14, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.channelInventoryVariance.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map(item => (
                      <tr key={item.listingId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{item.productName}</div>
                          <div style={{ marginTop: 3, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-subtle)', ...TEXT_WRAP_STYLE }}>{item.productSku}</div>
                        </td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <ChannelDot name={PROVIDER_DOT_NAMES[item.provider]} size={28} />
                            <strong style={{ color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{item.listingTitle || item.channelDisplayName}</strong>
                          </div>
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Pill tone={statusTone(item.connectionStatus)} size="sm">{t(`channels.status.${item.connectionStatus}`)}</Pill>
                            <Pill tone="neutral" size="sm">{t('pages.channelInventoryVariance.locations', { count: item.stockLocationCount })}</Pill>
                          </div>
                          <div style={{ marginTop: 5, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-subtle)', ...TEXT_WRAP_STYLE }}>
                            {item.listingSku || item.externalProductId || t('pages.channelInventoryVariance.noSku')}
                            {item.externalVariantId ? ` / ${item.externalVariantId}` : ''}
                          </div>
                        </td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(item.sourceAvailableQty, locale)}</td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(item.channelFiniteQuantity, locale)}</td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top', fontVariantNumeric: 'tabular-nums' }}>
                          <Pill tone={severityTone(item.severity)} size="sm">{formatNumber(item.differenceQty, locale)}</Pill>
                        </td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top' }}>
                          <div style={{ display: 'grid', gap: 7, alignItems: 'start' }}>
                            <Pill tone={severityTone(item.severity)} size="sm" dot>{t(`pages.channelInventoryVariance.severity.${item.severity}`)}</Pill>
                            <span style={{ color: 'var(--fg-muted)', fontSize: 12.5, ...TEXT_WRAP_STYLE }}>{t(`pages.channelInventoryVariance.types.${item.varianceType}`)}</span>
                          </div>
                        </td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, color: 'var(--fg-muted)', verticalAlign: 'top' }}>{formatDate(item.lastSeenAt)}</td>
                        <td style={{ paddingBlock: 13, paddingInline: 14, verticalAlign: 'top' }}>{renderItemActions(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {report.totalPages > 1 && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 14, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, fontWeight: 700 }}>{t('pages.channelInventoryVariance.paginationRange', { start: formatNumber(pageStart, locale), end: formatNumber(pageEnd, locale), total: formatNumber(report.totalElements, locale) })}</div>
                <div style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
                  <Button kind="secondary" size="sm" disabled={!hasPreviousPage || loading} onClick={() => setPageIndex(value => Math.max(0, value - 1))} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.previous')}</Button>
                  <Button kind="secondary" size="sm" disabled={!hasNextPage || loading} onClick={() => setPageIndex(value => value + 1)} style={mobileButtonStyle(isMobile)}>{t('pages.channelInventoryVariance.next')}</Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
